import type { Revision, RevisionPageParent } from '../../types';
import { mutations } from '../../utils';
import { isRevisionPageParent } from './guards';

/**
 * Get the parent of a page. It returns undefined if the parent is the root page.
 */
export function getPageParentInRevision(
    revision: Revision,
    pageId: string
): RevisionPageParent | undefined;
export function getPageParentInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string
): mutations.MutableData<RevisionPageParent> | undefined;
export function getPageParentInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string
): mutations.MutableData<RevisionPageParent> | undefined {
    const rootPages = mutations.get(revision, 'rootPages');

    if (mutations.includes(rootPages, pageId)) {
        return undefined;
    }

    for (const [, page] of mutations.entries(mutations.get(revision, 'pages'))) {
        if (!isRevisionPageParent(page)) {
            continue;
        }

        const pages = mutations.get(page, 'pages');
        if (mutations.includes(pages, pageId)) {
            return page;
        }
    }

    return undefined;
}

export const RootParentSymbol = Symbol('Root');

export type RevisionPageParentIndex = Map<string, string | typeof RootParentSymbol>;

const pageParentsCachedIndex = new WeakMap<
    mutations.MutableData<Revision>,
    RevisionPageParentIndex
>();

/**
 * Return a map from page ID to its parent ID within a revision.
 * This function does not use caching and always recomputes the index.
 *
 * If you need a cached version, use {@link getPageParentsIndexInImmutableRevision}.
 */
export function getPageParentsIndexInRevision(
    revision: mutations.MutableData<Revision>
): RevisionPageParentIndex {
    const result: RevisionPageParentIndex = new Map();

    const rootPages = mutations.get(revision, 'rootPages');
    mutations.values(rootPages).forEach((pageId) => {
        result.set(pageId, RootParentSymbol);
    });

    mutations.values(mutations.get(revision, 'pages')).forEach((page) => {
        if (!isRevisionPageParent(page)) {
            return;
        }

        const pageId = mutations.get(page, 'id');
        mutations.values(mutations.get(page, 'pages')).forEach((childId) => {
            result.set(childId, pageId);
        });
    });

    return result;
}

/**
 * Returns a map from page ID to its parent ID within a revision.
 * This version uses caching to avoid recomputation.
 *
 * **Important:** Only use this function when the revision is not changing between subsequent calls to the function.
 * In cases where the revision changes, use {@link getPageParentsIndexInRevision}.
 */
export function getPageParentsIndexInImmutableRevision(
    revision: Revision
): RevisionPageParentIndex {
    const revisionCachedParentIndex = pageParentsCachedIndex.get(revision);

    if (revisionCachedParentIndex) {
        return revisionCachedParentIndex;
    }

    const computedIndex = getPageParentsIndexInRevision(revision);
    pageParentsCachedIndex.set(revision, computedIndex);
    return computedIndex;
}
